import { Box, Button, Chip, Divider, InputAdornment, TextField, Typography } from '@mui/material';
import { Add, ExpandLess, ExpandMore, Search } from '@mui/icons-material';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import { useGetAccountAuthenticated } from '../session/swr/useGetAccountAuthenticated';
import { useAuth } from '../session/InternalAuthProvider';
import OrderPageCard from './OrderPageCard';
import { useLanguage } from '../common/GeneralUtils';
import { UseAuthPermissions } from '../session/UseAuthPermissions';
import { getOrderPages, getPassOrderPages } from '../utils/AccountUtils';
import { AccountData, OrderPageStatus, OrderPageTypes, PassOrderPageData } from '../api';
import PassOrderPageCard from '../passes/orderPages/PassOrderPageCard';
import './Orders.scss';
import { mb } from '../common/theme/mbTokens';

enum TypeFilter {
  ALL = 'all',
  EVENT = 'event',
  PRIVATE = 'private',
  COURSE = 'course',
  PASSES = 'passes',
}

const TYPE_ORDER = [OrderPageTypes.EVENT, OrderPageTypes.PRIVATE, OrderPageTypes.COURSE];

// Per-tab, so coming back from editing an activity lands where you left off, but a fresh
// session still starts at the top.
const SCROLL_STORAGE_KEY = 'webook-order-pages-scroll';

// Palette paths, resolved by `sx`. Kept in step with the card accents in OrderPageCard.
const FILTER_COLORS: Partial<Record<TypeFilter, string>> = {
  [TypeFilter.EVENT]: 'primary.main',
  [TypeFilter.PRIVATE]: 'text.primary',
  [TypeFilter.COURSE]: 'secondary.dark',
};

const OrderPages = () => {
  const language = useLanguage();
  const { readOnly } = UseAuthPermissions();
  const { t } = useTranslation();
  const passesEnabled = useFeatureFlagEnabled('feature-passes');

  // useAuth gives account data synchronously (loaded at app startup)
  // useGetAccountAuthenticated gives us the SWR mutator for local updates after archive/unarchive
  const { authState } = useAuth();
  const { updateLocalAccount } = useGetAccountAuthenticated({});
  const account = authState?.account as unknown as AccountData;

  const [typeFilter, setTypeFilter] = useState<TypeFilter>(TypeFilter.ALL);
  const [searchQuery, setSearchQuery] = useState('');
  const [archivedOpen, setArchivedOpen] = useState(false);
  const [archivedPassesOpen, setArchivedPassesOpen] = useState(false);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const scrollTopRef = useRef(0);
  const hasRestoredScrollRef = useRef(false);

  // Read off the ref on unmount rather than written on every scroll event, so scrolling a long
  // list stays free. The DOM node may already be detached by cleanup time, hence the ref.
  useEffect(
    () => () => {
      sessionStorage.setItem(SCROLL_STORAGE_KEY, String(scrollTopRef.current));
    },
    [],
  );

  const activityLabel = account?.activity_label_singular?.[language];
  const activityLabelPlural = account?.activity_label_plural?.[language];

  const sortPasses = (pages: PassOrderPageData[]) =>
    [...pages].sort((a, b) => {
      if (a.price !== b.price) return a.price - b.price;
      return a.validity_days - b.validity_days;
    });

  const allPassOrderPages = getPassOrderPages(account, true);
  const activePassOrderPages = allPassOrderPages.filter((p) => p.status !== OrderPageStatus.ARCHIVED);
  const archivedPassOrderPages = allPassOrderPages.filter((p) => p.status === OrderPageStatus.ARCHIVED);
  const sortedPassOrderPages = sortPasses(activePassOrderPages);
  const sortedArchivedPassOrderPages = sortPasses(archivedPassOrderPages);

  const allPages = getOrderPages(account, true);
  const activePages = allPages.filter((p) => p.status !== OrderPageStatus.ARCHIVED);
  const archivedPages = allPages.filter((p) => p.status === OrderPageStatus.ARCHIVED);

  const sortedActivePages = [...activePages].sort(
    (a, b) => TYPE_ORDER.indexOf(a.order_page_type) - TYPE_ORDER.indexOf(b.order_page_type),
  );

  const passesPerOrderPage: Record<string, string[]> = {};
  const passRedeemableIds = new Set<string>();
  activePassOrderPages.forEach((pass) => {
    pass.redeemable_on?.forEach((item) => {
      passRedeemableIds.add(item.order_page_id);
      if (!passesPerOrderPage[item.order_page_id]) {
        passesPerOrderPage[item.order_page_id] = [];
      }
      const passLabel = pass.label?.[language];
      if (passLabel) {
        passesPerOrderPage[item.order_page_id].push(passLabel);
      }
    });
  });

  const workshopPages = sortedActivePages.filter((p) => !passRedeemableIds.has(p.id));

  const eventCount = workshopPages.filter((p) => p.order_page_type === OrderPageTypes.EVENT).length;
  const privateCount = workshopPages.filter((p) => p.order_page_type === OrderPageTypes.PRIVATE).length;
  const courseCount = workshopPages.filter((p) => p.order_page_type === OrderPageTypes.COURSE).length;

  const filteredPages = workshopPages.filter((p) => {
    const matchesType =
      typeFilter === TypeFilter.ALL ? true : p.order_page_type === (typeFilter as string as OrderPageTypes);
    const matchesSearch = !searchQuery || (p.label[language] ?? '').toLowerCase().includes(searchQuery.toLowerCase());
    return matchesType && matchesSearch;
  });

  const filteredPassOrderPages = sortedPassOrderPages.filter(
    (p) => !searchQuery || (p.label?.[language] ?? '').toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const filteredArchivedPassOrderPages = sortedArchivedPassOrderPages.filter(
    (p) => !searchQuery || (p.label?.[language] ?? '').toLowerCase().includes(searchQuery.toLowerCase()),
  );
  const filteredRedeemablePages = sortedActivePages
    .filter((p) => passRedeemableIds.has(p.id))
    .filter((p) => !searchQuery || (p.label[language] ?? '').toLowerCase().includes(searchQuery.toLowerCase()));

  const handleFilterChange = (filter: TypeFilter) => {
    setTypeFilter(filter);
  };

  const passesCount = sortedPassOrderPages.length + sortedActivePages.filter((p) => passRedeemableIds.has(p.id)).length;

  // Restored once the cards exist, otherwise the container is still too short to scroll and the
  // assignment clamps to 0. Cards are fixed-width with no images, so layout is stable on first paint.
  const renderedCardCount = sortedActivePages.length + sortedPassOrderPages.length;
  useLayoutEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || hasRestoredScrollRef.current || renderedCardCount === 0) {
      return;
    }
    hasRestoredScrollRef.current = true;
    const saved = Number(sessionStorage.getItem(SCROLL_STORAGE_KEY));
    if (saved > 0) {
      container.scrollTop = saved;
      scrollTopRef.current = container.scrollTop;
    }
  }, [renderedCardCount]);

  const filterChips = [
    { value: TypeFilter.ALL, label: `${t('order_pages.all')} (${workshopPages.length})` },
    { value: TypeFilter.EVENT, label: `${t('order_pages.join')} (${eventCount})` },
    { value: TypeFilter.PRIVATE, label: `${t('order_pages.private')} (${privateCount})` },
    { value: TypeFilter.COURSE, label: `${t('order_pages.course')} (${courseCount})` },
    ...(passesEnabled ? [{ value: TypeFilter.PASSES, label: `${t('main_sidebar.passes')} (${passesCount})` }] : []),
  ];

  const sectionLabels: Record<string, string> = {
    [OrderPageTypes.EVENT]: t('order_pages.events'),
    [OrderPageTypes.PRIVATE]: t('order_pages.private_label'),
    [OrderPageTypes.COURSE]: t('order_pages.courses'),
  };

  const renderCardGrid = (pages: typeof filteredPages) => (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
      {pages.map((p) => (
        <OrderPageCard
          key={p.id}
          orderPage={p}
          isPartOfPass={passRedeemableIds.has(p.id)}
          passNames={passesPerOrderPage[p.id] ?? []}
          isArchived={false}
          updateLocalAccount={updateLocalAccount}
        />
      ))}
    </Box>
  );

  const renderCards = () => {
    if (typeFilter === TypeFilter.PASSES) {
      return null;
    }
    if (typeFilter !== TypeFilter.ALL) {
      return renderCardGrid(filteredPages);
    }

    return (
      <>
        {TYPE_ORDER.map((type) => {
          const group = filteredPages.filter((p) => p.order_page_type === type);
          if (group.length === 0) return null;
          return (
            <Box key={type} sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="h6" fontWeight={700}>
                {sectionLabels[type]}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {group.map((p) => (
                  <OrderPageCard
                    key={p.id}
                    orderPage={p}
                    isPartOfPass={false}
                    passNames={[]}
                    isArchived={false}
                    updateLocalAccount={updateLocalAccount}
                  />
                ))}
              </Box>
            </Box>
          );
        })}
      </>
    );
  };

  return (
    <Box
      ref={scrollContainerRef}
      onScroll={(e) => {
        scrollTopRef.current = e.currentTarget.scrollTop;
      }}
      sx={{
        p: mb.layout.cardPadding,
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
      }}>
      <HelmetProvider>
        <Helmet>
          <title>Monkeybook - {t('main_sidebar.order_pages', { activityLabel: activityLabelPlural })}</title>
        </Helmet>
      </HelmetProvider>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          {t('main_sidebar.order_pages', { activityLabel: activityLabelPlural })}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          {passesEnabled && (
            <Link className="no-style-link" to={readOnly ? '#' : '/dashboard/create-pass'}>
              <Button variant="outlined" color="primary" disabled={readOnly} startIcon={<Add />}>
                {t('passes.create_pass_product')}
              </Button>
            </Link>
          )}
          <Link className="no-style-link" to={readOnly ? '#' : '/dashboard/create-experience'}>
            <Button variant="contained" color="primary" disabled={readOnly} startIcon={<Add />}>
              {t('order_pages.add_new', { activityLabel })}
            </Button>
          </Link>
        </Box>
      </Box>

      {sortedPassOrderPages.length + activePages.length > 4 && (
        <TextField
          size="small"
          placeholder={t('order_pages.search')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ width: 240 }}
        />
      )}

      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
        {filterChips.map((c) => {
          const isSelected = typeFilter === c.value;
          const accentColor = FILTER_COLORS[c.value];
          return (
            <Chip
              key={c.value}
              label={c.label}
              onClick={() => handleFilterChange(c.value)}
              variant={isSelected ? 'filled' : 'outlined'}
              // Filter tabs per the spec: the selected one is an ink fill (the page keeps one green button); the others
              // stay outlined in their type's accent.
              sx={
                isSelected
                  ? {
                      bgcolor: 'text.primary',
                      color: 'background.paper',
                      '&:hover': { bgcolor: 'text.primary' },
                    }
                  : accentColor
                  ? { borderColor: accentColor, color: accentColor }
                  : {}
              }
            />
          );
        })}
      </Box>

      {renderCards()}

      {passesEnabled && (typeFilter === TypeFilter.ALL || typeFilter === TypeFilter.PASSES) && (
        <>
          <Divider />
          <Typography variant="h6" fontWeight={700}>
            {t('main_sidebar.passes')}
          </Typography>
          {filteredRedeemablePages.length > 0 && (
            <Typography variant="subtitle2" color="text.secondary">
              {t('passes.pass_products')}
            </Typography>
          )}
          {filteredPassOrderPages.length === 0 && !searchQuery ? (
            <Typography variant="body2" color="text.secondary">
              {t('passes.empty_state_subtitle')}
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {filteredPassOrderPages.map((passOrderPage) => (
                <PassOrderPageCard key={passOrderPage.id} passOrderPage={passOrderPage} />
              ))}
            </Box>
          )}
          {filteredRedeemablePages.length > 0 && (
            <>
              <Typography variant="subtitle2" color="text.secondary">
                {t('passes.redeemable_workshops')}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {filteredRedeemablePages.map((p) => (
                  <OrderPageCard
                    key={p.id}
                    orderPage={p}
                    isPartOfPass
                    passNames={passesPerOrderPage[p.id] ?? []}
                    isArchived={false}
                    updateLocalAccount={updateLocalAccount}
                  />
                ))}
              </Box>
            </>
          )}
        </>
      )}

      {typeFilter === TypeFilter.ALL && archivedPages.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 0.5 }}
            onClick={() => setArchivedOpen((o) => !o)}>
            <Typography variant="h6" fontWeight={700}>
              {t('order_pages.archived_workshops', { activityLabel: activityLabelPlural })}
            </Typography>
            {archivedOpen ? <ExpandLess /> : <ExpandMore />}
          </Box>
          {archivedOpen && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {archivedPages.map((p) => (
                <OrderPageCard
                  key={p.id}
                  orderPage={p}
                  isPartOfPass={passRedeemableIds.has(p.id)}
                  passNames={passesPerOrderPage[p.id] ?? []}
                  isArchived
                  updateLocalAccount={updateLocalAccount}
                />
              ))}
            </Box>
          )}
        </>
      )}

      {passesEnabled &&
        (typeFilter === TypeFilter.ALL || typeFilter === TypeFilter.PASSES) &&
        filteredArchivedPassOrderPages.length > 0 && (
          <>
            <Divider />
            <Box
              sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer', gap: 0.5 }}
              onClick={() => setArchivedPassesOpen((o) => !o)}>
              <Typography variant="h6" fontWeight={700}>
                {t('order_pages.archived_passes')}
              </Typography>
              {archivedPassesOpen ? <ExpandLess /> : <ExpandMore />}
            </Box>
            {archivedPassesOpen && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                {filteredArchivedPassOrderPages.map((passOrderPage) => (
                  <PassOrderPageCard key={passOrderPage.id} passOrderPage={passOrderPage} />
                ))}
              </Box>
            )}
          </>
        )}
    </Box>
  );
};

export default OrderPages;
